import React, { useRef, useState, useMemo, forwardRef, useCallback, useEffect } from 'react';
import _ from 'lodash';
import { Form, Alert } from 'antd'
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes } from 'utils/formUtils';
import GenericMappingForm from '../GenericMappingForm';
import moment from 'moment';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
import Vocabulary from "components/Vocabulary";

const FiniteStateMappingForm = forwardRef(({ disableProjectFields = false, record, ...props }, ref) => {
  const featureFlags = useFeatureFlag();

  const [organisationData, setOrganisationData] = useState({});

  useEffect(() => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericLoginDetail(props.toolName);
    promise
      .then((response) => {
        const organisationData = response.configurations.find(config => config.id === props.loginConfigId);
        setOrganisationData(organisationData);
      });

    return cancel;
  }, [props.toolName, props.loginConfigId]);

  const {
    orgId: organisationId,
    orgName: organisationName,
    graph_ql,
    nextGenAPI = "false"
  } = props.loginConfig || organisationData;
  const [organisations] = useState(() => [{ label: organisationName, value: organisationId }]);
  const [firmwaresInitialValue] = useState(() => {
    if (!record) {
      return {};
    }
    const firmwareIds = _.split(record.firmwareId, ',');
    const firmwareNames = _.split(record.firmwareName, ',');
    return { [record.projectId]: _.reduce(firmwareIds, (intialValues, firmwareId, index) => {
      intialValues[firmwareId] = firmwareNames[index];
      return intialValues;
    }, {})};
  });
  // const organisationsRef = useRef(record ? [{ id: record.orgId, name: record.orgName }] : []);
  const projectsRef = useRef(record ? { [record.projectId]: record.projectName } : {});
  const firmwaresRef = useRef(firmwaresInitialValue);
  const apiParamsRef = useRef({});
  const [form] = Form.useForm();

  const fetchResponse = useCallback((params) => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, ...params });
    return {
      promise: promise.then(response => {
         
        if (['product', 'project'].includes(params.type)) {
          if (!_.isEmpty(projectsRef.current) && !_.isEmpty(response.projects)) {
            const projectsArray = Object.keys(projectsRef.current).map(key => ({
              name: projectsRef.current[key],
              id: key,
            }));
            response.projects = _.uniqBy([...response.projects, ...projectsArray], 'id');
          }
          _.forEach(response.projects, (data) => {
            projectsRef.current[data.id] = data.name;
          });
        } else {
          if (!_.isEmpty(firmwaresRef.current) && !_.isEmpty(firmwaresRef.current[params.project]) && !_.isEmpty(response.projects)) {
            const firmwaresArray = Object.keys(firmwaresRef.current[params.project]).map(key => ({
              id: key,
              name: firmwaresRef.current[params.project][key]
            }));
            response.projects = _.uniqBy([...response.projects, ...firmwaresArray], 'id')
          }
          // Initialize project key if it doesn't exist
          if (!firmwaresRef.current[params.project]) {
            firmwaresRef.current[params.project] = {};
          }
          _.forEach(response.projects, (data) => {
            firmwaresRef.current[params.project][data.id] = data.name;
          });
        }
        return response;
      }),
      cancel
    }
  }, [props.loginConfigId, props.toolName]);

  const onProjectChange = useCallback((value, field) => {
    const { name } = field;
    const configurations = form.getFieldValue("configurations");
    const updatedConfiguration = [
      ...configurations,
    ];
    updatedConfiguration[name] = {
      ...updatedConfiguration[name],
      firmwareId: undefined,
    }
    form.setFieldsValue({ configurations: updatedConfiguration });
  }, [form]);

  const getExtraFieldsData = useCallback(({ projectId, firmwareId, addFutureFirmware, id }) => {
    return ({
      projectId,
      id,
      projectName: projectsRef.current[projectId],
      firmwareId: _.join(firmwareId),
      firmwareName: _.map(firmwareId, (fwId) => firmwaresRef.current[projectId][fwId]).join(),
      ...(!_.isNil(addFutureFirmware) && { addFutureFirmware }),
      ...(addFutureFirmware && { lastFirmwareUpdateDate: moment().valueOf() })
    });
  }, []);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { firmwareId, firmwareName, addFutureFirmware } = config;
    return {
      firmwareId: _.split(firmwareId, ','),
      firmwareName: _.split(firmwareName, ','),
      ...(addFutureFirmware && { addFutureFirmware: JSON.parse(addFutureFirmware) }) // we get boolean value as a string
    }
  }, []);

  const extraFields = useMemo(() => (
    (featureFlags.FINITE_STATE_NEXT_GEN_API && nextGenAPI === "true" ? [
      {
        name: 'projectId',
        colSpan: '12',
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        label: 'Project Name',
        disabled: disableProjectFields,
        required: true,
        props: {
          api: fetchResponse,
          apiParams: { type: 'project' },
          allowClear: true,
          onChange: onProjectChange,
          autoFocus: !disableProjectFields,
        }
      },
      {
        name: 'firmwareId',
        colSpan: 12,
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        label: 'Versions',
        required: true,
        disabled: disableProjectFields,
        props: {
          setExtraProps: (fieldProps, currentField) => {
            const configurations = form.getFieldValue("configurations");
            const currentConfiguration = configurations[currentField.name] || {};
            const projectId = currentConfiguration?.projectId;
            const apiParams = apiParamsRef.current;
            const keyName = `${currentField.name}_${projectId}`;
            if (!apiParams[keyName] && projectId) {
              apiParams[keyName] = { project: projectId, type: 'version' };
            }
            return {
              mode: "multiple",
              api: projectId && fetchResponse,
              allowClear: true,
              apiParams: apiParams[keyName],
              resetOptionsProp: apiParams[keyName],
            }
          },
        }
      }
    ] : [
      {
        name: 'orgId',
        colSpan: 12,
        type: FieldTypes.SELECT,
        label: 'Portfolio',
        disabled: true,
        options: organisations,
        props: {
          setExtraProps: (fieldProps, currentField) => {
            return {
              defaultValue: organisationName,
            }
          },
        }
      },
      {
        name: 'projectId',
        colSpan: 12,
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        label: 'FiniteState Artifacts',
        disabled: disableProjectFields,
        required: true,
        props: {
          autoFocus: !disableProjectFields,
          api: organisationId && fetchResponse,
          setExtraProps: (fieldProps, currentField) => {
            const apiParams = apiParamsRef.current;
            const keyName = `${currentField.name}_${organisationId}`;
            if (!apiParams[keyName] && organisationId) {
              apiParams[keyName] = { organisation: organisationId, type: 'product' };
            }
            return {
              apiParams: apiParams[keyName],
              resetOptionsProp: apiParams[keyName],
            }
          },
          onChange: onProjectChange,
        },
      },
      {
        name: 'firmwareId',
        colSpan: 12,
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        label: 'Firmware Version',
        required: true,
        disabled: disableProjectFields,
        props: {
          setExtraProps: (fieldProps, currentField) => {
            const configurations = form.getFieldValue("configurations");
            const currentConfiguration = configurations[currentField.name] || {};
            const apiParams = apiParamsRef.current;
            const keyName = `${currentField.name}_${organisationId}_${currentConfiguration.projectId}`;
            if (!apiParams[keyName] && organisationId && currentConfiguration.projectId) {
              apiParams[keyName] = { organisation: organisationId, project: currentConfiguration.projectId, type: 'firmware' };
            }
            return {
              mode: "multiple",
              api: organisationId && currentConfiguration.projectId && fetchResponse,
              resetOptionsProp: apiParams[keyName],
              apiParams: apiParams[keyName],
            }
          },
        }
      },
      ...(graph_ql === 'true'
        ?
        [
          ...(featureFlags.VERSIONS
            ? [
              {
                colSpan: 24,
                render: () => (
                  <Alert
                    className="m-b-m"
                    message={
                      <>
                        Mapping to add Future Finite State versions is now supported
                        via Runbooks allowing the ability to create version{" "}
                        <Vocabulary name="subProduct" /> automatically
                      </>
                    }
                    type="warning"
                  />
                ),
              },
            ]
            : [
              {
                name: 'addFutureFirmware',
                type: FieldTypes.BOOLEAN,
                label: 'Add Future Firmware Versions',
                disabled: disableProjectFields,
                inline: true
              },
            ]),
        ] : []),
    ])
  ), [organisations, disableProjectFields, fetchResponse, onProjectChange, graph_ql, featureFlags, organisationName, organisationId, form]);

  let recordWithoutId = record;

  if (record?.id !== undefined) {
    const { id, ...rest } = record;
    recordWithoutId = rest;
  }


  return (
    <GenericMappingForm
      form={form}
      ref={ref}
      getExtraFieldsData={getExtraFieldsData}
      extraFields={extraFields}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      record={props.isUnmapped ? recordWithoutId : record}
      {...props}
    />
  )
})

export default FiniteStateMappingForm;